import type { JSX, ReactNode } from 'react';

/**
 * Minimal Markdown renderer for AI chat replies.
 *
 * The model reliably answers financial questions with GFM tables ("show me a
 * breakdown by company"), which the chat bubble previously printed as raw pipes
 * because it just dumped text into `white-space: pre-wrap`.
 *
 * Deliberately hand-rolled rather than pulling in react-markdown + remark-gfm:
 * the CI audit gate fails the build on any high CVE in the dependency tree, the
 * web bundle is already over the size warning, and the subset the model emits
 * is small and predictable. If the model ever starts emitting something richer
 * than this handles, prefer growing this file over adding a parser dependency —
 * unhandled syntax degrades to plain text, never to a broken page.
 *
 * Everything is built as React elements. There is no dangerouslySetInnerHTML
 * anywhere, so model output cannot inject markup.
 */

/** Split inline `**bold**`, `*italic*`, `_italic_` and `` `code` `` into nodes. */
function renderInline(text: string, keyPrefix: string): ReactNode[] {
  const out: ReactNode[] = [];
  // Order matters: ** before * so bold wins over italic.
  const pattern = /(\*\*[^*]+\*\*|`[^`]+`|\*[^*]+\*|_[^_]+_)/g;
  let last = 0;
  let match: RegExpExecArray | null;
  let i = 0;

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > last) out.push(text.slice(last, match.index));
    const tok = match[0];
    const key = `${keyPrefix}-i${i++}`;

    if (tok.startsWith('**')) {
      out.push(<strong key={key}>{tok.slice(2, -2)}</strong>);
    } else if (tok.startsWith('`')) {
      out.push(
        <code
          key={key}
          style={{
            background: 'var(--bg-surface-3, rgba(127,127,127,0.18))',
            padding: '1px 4px',
            borderRadius: 4,
            fontFamily: 'ui-monospace, monospace',
            fontSize: '0.92em',
          }}
        >
          {tok.slice(1, -1)}
        </code>,
      );
    } else {
      out.push(<em key={key}>{tok.slice(1, -1)}</em>);
    }
    last = match.index + tok.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

/** A `| a | b |` row split into trimmed cells, tolerating optional edge pipes. */
function splitRow(line: string): string[] {
  let s = line.trim();
  if (s.startsWith('|')) s = s.slice(1);
  if (s.endsWith('|')) s = s.slice(0, -1);
  return s.split('|').map((c) => c.trim());
}

/** `|---|:--:|` — the line that makes the row above it a real table header. */
function isTableSeparator(line: string): boolean {
  const cells = splitRow(line);
  return cells.length > 0 && cells.every((c) => /^:?-{1,}:?$/.test(c));
}

function isTableRow(line: string): boolean {
  return line.includes('|');
}

export function MarkdownMessage({ text }: { text: string }): JSX.Element {
  const lines = text.replace(/\r\n/g, '\n').split('\n');
  const blocks: ReactNode[] = [];
  let paragraph: string[] = [];
  let key = 0;

  const flushParagraph = () => {
    if (paragraph.length === 0) return;
    const body = paragraph.join('\n');
    blocks.push(
      <p key={`p${key++}`} style={{ margin: '0 0 8px', whiteSpace: 'pre-wrap' }}>
        {renderInline(body, `p${key}`)}
      </p>,
    );
    paragraph = [];
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]!;

    // ── fenced code ──────────────────────────────────────────────────────
    if (line.trim().startsWith('```')) {
      flushParagraph();
      const buf: string[] = [];
      i++;
      while (i < lines.length && !lines[i]!.trim().startsWith('```')) {
        buf.push(lines[i]!);
        i++;
      }
      blocks.push(
        <pre
          key={`c${key++}`}
          style={{
            background: 'var(--bg-surface-3, rgba(127,127,127,0.15))',
            padding: 10,
            borderRadius: 6,
            overflowX: 'auto',
            margin: '0 0 8px',
            fontSize: 12,
          }}
        >
          <code>{buf.join('\n')}</code>
        </pre>,
      );
      continue;
    }

    // ── table: a row followed by a separator ─────────────────────────────
    if (isTableRow(line) && i + 1 < lines.length && isTableSeparator(lines[i + 1]!)) {
      flushParagraph();
      const header = splitRow(line);
      i += 2;
      const rows: string[][] = [];
      while (i < lines.length && isTableRow(lines[i]!) && lines[i]!.trim() !== '') {
        rows.push(splitRow(lines[i]!));
        i++;
      }
      i--; // the outer loop will advance past the last consumed line

      blocks.push(
        // The bubble is width-capped, so a wide table has to scroll inside it
        // rather than push the chat column sideways.
        <div key={`t${key++}`} style={{ overflowX: 'auto', margin: '0 0 8px' }}>
          <table className="table" style={{ fontSize: 13 }}>
            <thead>
              <tr>
                {header.map((h, hi) => (
                  <th key={hi}>{renderInline(h, `th${hi}`)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, ri) => (
                <tr key={ri}>
                  {/* Pad short rows so a ragged reply can't drop cells. */}
                  {header.map((_, ci) => (
                    <td key={ci}>{renderInline(r[ci] ?? '', `td${ri}-${ci}`)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      );
      continue;
    }

    // ── heading ──────────────────────────────────────────────────────────
    const heading = /^(#{1,6})\s+(.*)$/.exec(line);
    if (heading) {
      flushParagraph();
      const level = heading[1]!.length;
      blocks.push(
        <div
          key={`h${key++}`}
          style={{
            fontWeight: 600,
            fontSize: level <= 2 ? 15 : 14,
            margin: '6px 0 4px',
          }}
        >
          {renderInline(heading[2]!, `h${key}`)}
        </div>,
      );
      continue;
    }

    // ── list item ────────────────────────────────────────────────────────
    const bullet = /^\s*[-*]\s+(.*)$/.exec(line);
    const numbered = /^\s*(\d+)\.\s+(.*)$/.exec(line);
    if (bullet || numbered) {
      flushParagraph();
      const marker = bullet ? '•' : `${numbered![1]}.`;
      const body = bullet ? bullet[1]! : numbered![2]!;
      blocks.push(
        <div key={`l${key++}`} style={{ display: 'flex', gap: 8, margin: '0 0 3px' }}>
          <span style={{ opacity: 0.7, flexShrink: 0 }}>{marker}</span>
          <span>{renderInline(body, `l${key}`)}</span>
        </div>,
      );
      continue;
    }

    // ── blank line ends a paragraph ──────────────────────────────────────
    if (line.trim() === '') {
      flushParagraph();
      continue;
    }

    paragraph.push(line);
  }
  flushParagraph();

  return <div>{blocks}</div>;
}
